// Global cache to store scale data when it's encountered in other components
let scaleDataCache = {};

/**
 * Store scale data so it can be accessed by the prompt generator
 * @param {string} eventId - The ID of the event
 * @param {Array} scaleOptions - The scale options array (containing value distribution)
 * @param {Object} scaleMetrics - Additional metrics for scale questions
 */
export const storeScaleData = (eventId, scaleOptions, scaleMetrics = {}) => {
  if (!eventId || !scaleOptions || !Array.isArray(scaleOptions)) {
    console.warn('⚠️ Invalid data passed to storeScaleData', { eventId, hasOptions: !!scaleOptions, isArray: Array.isArray(scaleOptions) });
    return;
  }
  
  // console.log('📥 Storing scale data for eventId:', eventId, { 
  //   count: scaleOptions.length, 
  //   firstItem: scaleOptions[0],
  //   metrics: scaleMetrics,
  //   timestamp: new Date().toISOString() 
  // });
  
  scaleDataCache[eventId] = {
    options: scaleOptions,
    metrics: scaleMetrics
  };
};

/**
 * Get stored scale data for an event
 * @param {string} eventId - The ID of the event
 * @returns {Object|null} The scale data or null if not found
 */
export const getStoredScaleData = (eventId) => {
  if (!eventId || !scaleDataCache[eventId]) {
    return null;
  }
  return scaleDataCache[eventId];
};

/**
 * Clear the scale data cache
 */
export const clearScaleDataCache = () => {
  scaleDataCache = {};
  // console.log('🧺 Scale data cache cleared');
};

/**
 * Fallback function in case of errors
 * @returns {string} A fallback prompt for scale analysis
 */
export const getScaleFallbackPrompt = () => {
  return `You are an expert data analyst analyzing a Survey with a Scale question. Scale questions allow respondents to rate something on a numerical scale (typically 1-5 or 1-10).

Due to limited data availability, please provide general analysis principles for interpreting scale-based survey data, including:

1. How to identify consensus vs. divergent opinions in scale responses
2. Best practices for analyzing rating distributions
3. Methods for determining significant patterns in scale data
4. Techniques for converting scale ratings into actionable insights
5. How to communicate scale-based results effectively

Structure your response with these EXACT section headings using single # for each section:

# Executive Summary
# Key Themes
# Recommendations
# Limitations
# Survey Design Feedback`;
};

/**
 * Generate a prompt for scale question analysis
 * @param {Object} eventDetails - The event details
 * @param {Object} stats - The event statistics
 * @param {Array} posts - The event posts (comments)
 * @returns {string} The generated prompt
 */
export const generateScalePrompt = (eventDetails, stats, posts = []) => {
  // console.log('⚙️ generateScalePrompt called with:', {
  //   hasEventDetails: !!eventDetails,
  //   eventDetailsKeys: eventDetails ? Object.keys(eventDetails) : [],
  //   hasStats: !!stats,
  //   statsKeys: stats ? Object.keys(stats) : [],
  //   hasPosts: !!posts,
  //   postsCount: Array.isArray(posts) ? posts.length : 0,
  //   timestamp: new Date().toISOString()
  // });

  if (!eventDetails) {
    console.error('❌ Missing eventDetails in generateScalePrompt');
    return getScaleFallbackPrompt();
  }
  
  if (!stats) {
    console.warn('⚠️ Missing stats in generateScalePrompt, continuing with limited data');
  }

  try {
    // Format event details and stats for the prompt
    const eventName = eventDetails?.name || eventDetails?.title || 'N/A';
    let questionType = eventDetails?.question_type || 'scale';
    
    // Determine scale range
    let scaleRange = 5; // Default to 5-point scale
    if (questionType === 'scale_5') {
      scaleRange = 5;
      questionType = '5-point scale';
    } else if (questionType === 'scale_10') {
      scaleRange = 10;
      questionType = '10-point scale';
    } else if (questionType === 'scale') {
      // Try to determine scale range from data
      scaleRange = 5; // Default fallback
    }
    
    const description = eventDetails?.description || eventDetails?.content || 'N/A';
    
    // Log key details for debugging
    // console.log('🔍 Scale question details:', {
    //   eventName,
    //   questionType,
    //   scaleRange,
    //   description,
    //   timestamp: new Date().toISOString()
    // });
    
    // Access metrics data from the correct path
    const metricsData = stats?.metrics || {};
    const summaryData = stats?.summary || {};
    
    // Extract total responses with comprehensive fallback paths
    let totalResponses = 0;
    
    // Try all possible paths for total responses in metrics
    if (metricsData && typeof metricsData === 'object') {
      totalResponses = metricsData.choices || metricsData.contributed || metricsData.responses || 
                      metricsData.total_activities || metricsData.totalResponses || metricsData.total || 0;
    }
    
    // If summary has total responses info, use that as a fallback
    if (totalResponses === 0 && summaryData && typeof summaryData === 'object') {
      totalResponses = summaryData.total_choices || summaryData.total_responses || 
                     summaryData.choices || summaryData.responses || summaryData.contributed || 0;
    }
    
    // Check top level stats as a last resort
    if (totalResponses === 0) {
      totalResponses = stats.total_choices || stats.total_responses || stats.choices || 
                     stats.responses || stats.contributed || stats.total_activities || 0;
    }
    
    // console.log('📊 Scale prompt - complete search for totalResponses:', { 
    //   totalResponses, 
    //   metricsData_paths: metricsData ? {
    //     choices: metricsData.choices,
    //     contributed: metricsData.contributed,
    //     responses: metricsData.responses,
    //     total_activities: metricsData.total_activities
    //   } : 'No metrics data',
    //   summaryData_paths: summaryData ? {
    //     total_choices: summaryData.total_choices,
    //     total_responses: summaryData.total_responses,
    //     choices: summaryData.choices
    //   } : 'No summary data',
    //   timestamp: new Date().toISOString() 
    // });
    
    // Similarly extract total users/participants with comprehensive fallback paths
    let totalUsers = 0;
    
    // Try all possible paths for totalUsers in metrics
    if (metricsData && typeof metricsData === 'object') {
      totalUsers = metricsData.total_user || metricsData.total_users || metricsData.joined || 
                 metricsData.viewed_user || metricsData.participants || metricsData.contributors || 0;
    }
    
    // If summary has total users, use that as a fallback
    if (totalUsers === 0 && summaryData && typeof summaryData === 'object') {
      totalUsers = summaryData.total_user || summaryData.total_users || summaryData.users || 
                 summaryData.participants || summaryData.contributors || summaryData.joined || 0;
    }
    
    // Check top level stats as a last resort
    if (totalUsers === 0) {
      totalUsers = stats.total_user || stats.total_users || stats.users || 
                 stats.participants || stats.joined || stats.viewers || 0;
    }
    
    // Calculate participation rate with validation
    let participationRate = 0;
    
    // Try to get it directly from metrics or summary first
    if (metricsData?.participation_rate !== undefined && metricsData?.participation_rate !== null) {
      participationRate = metricsData.participation_rate;
    } else if (summaryData?.participation_rate !== undefined && summaryData?.participation_rate !== null) {
      participationRate = summaryData.participation_rate;
    } else if (stats?.participation_rate !== undefined && stats?.participation_rate !== null) {
      participationRate = stats.participation_rate;
    }
    // Otherwise calculate it if we have valid totalUsers
    else if (totalUsers > 0 && totalResponses > 0) {
      participationRate = Math.round((totalResponses / totalUsers) * 100);
      // console.log('📊 Calculating participation rate from real data:', participationRate + '%');
    }
    
    // Extract average score with enhanced validation
    let averageScore = null;
    
    // Try to find the average score in all possible locations
    if (summaryData?.simple_score !== undefined && summaryData?.simple_score !== null) {
      averageScore = summaryData.simple_score;
    } else if (summaryData?.simple_score_display !== undefined && summaryData?.simple_score_display !== null) {
      averageScore = summaryData.simple_score_display;
    } else if (stats?.simple_score !== undefined && stats?.simple_score !== null) {
      averageScore = stats.simple_score;
    } else if (stats?.simple_score_display !== undefined && stats?.simple_score_display !== null) {
      averageScore = stats.simple_score_display;
    }
    
    // console.log('📊 Scale prompt metrics extraction:', { 
    //   totalResponses, 
    //   totalUsers, 
    //   participationRate, 
    //   averageScore, 
    //   timestamp: new Date().toISOString() 
    // });
    
    // Check for cached scale data for this event
    const currentEventId = eventDetails.id || eventDetails.eventId;
    const cachedScaleData = getStoredScaleData(currentEventId);
    
    // console.log('🔍 Checking cached scale data:', { 
    //   eventId: currentEventId, 
    //   hasCachedData: !!cachedScaleData,
    //   timestamp: new Date().toISOString() 
    // });
    
    // Extract scale distribution data
    let scaleDistribution = [];
    
    if (cachedScaleData && Array.isArray(cachedScaleData.options) && cachedScaleData.options.length > 0) {
      // console.log('✅ Using cached scale data:', { 
      //   optionCount: cachedScaleData.options.length,
      //   sampleOption: cachedScaleData.options[0],
      //   metrics: cachedScaleData.metrics,
      //   timestamp: new Date().toISOString() 
      // });
      
      // Map cached data to prompt format for Ideas model
      const Ideas = {
        getName: (idea) => idea.name || idea.choice || idea.title || 'N/A',
        getDescription: (idea) => idea.description || '',
        getChoiceCount: (idea) => idea.choiceCount || 0,
        getChoicePercent: (idea) => idea.choicePercent || idea.choice_pct || '0%'
      };
      
      // Map cached options to distribution format
      scaleDistribution = cachedScaleData.options.map(option => ({
        value: Ideas.getName(option),
        description: Ideas.getDescription(option),
        count: Ideas.getChoiceCount(option),
        percentage: Ideas.getChoicePercent(option),
        // Convert percentage string to number for later calculations
        percentValue: parseInt((Ideas.getChoicePercent(option) || '0%').replace('%', ''), 10)
      }));
      
      // Sort by scale value numerically if possible
      scaleDistribution.sort((a, b) => {
        const aVal = parseInt(a.value, 10);
        const bVal = parseInt(b.value, 10);
        if (!isNaN(aVal) && !isNaN(bVal)) {
          return aVal - bVal;
        }
        return a.value.localeCompare(b.value);
      });
      
      // console.log('📊 Processed scale distribution:', { 
      //   count: scaleDistribution.length,
      //   items: scaleDistribution,
      //   timestamp: new Date().toISOString() 
      // });
    }
    
    // Extract comments from posts parameter (most reliable source)
    let comments = [];
    if (Array.isArray(posts) && posts.length > 0) {
      // console.log('🗣️ Using posts for comments:', { count: posts.length });
      comments = posts.map(post => ({
        content: post.content || post.text || post.body || post.comment || post.message || '',
        author: post.author || post.user || post.username || 'Anonymous',
        likes: post.likes || post.like_count || 0,
        date: post.date || post.created_at || post.timestamp || ''
      }));
    }
    
    // Extract additional scale insights if available from cached data
    let distributionInsights = {
      mostCommonRating: 'Unknown',
      mostCommonPercentage: 0,
      leastCommonRating: 'Unknown',
      leastCommonPercentage: 0,
      belowMidpointPercentage: 0,
      aboveMidpointPercentage: 0,
      atMidpointPercentage: 0
    };
    
    if (scaleDistribution.length > 0) {
      // Find most and least common ratings
      let mostCommon = scaleDistribution[0];
      let leastCommon = scaleDistribution[0];
      
      for (const item of scaleDistribution) {
        if (item.percentValue > mostCommon.percentValue) {
          mostCommon = item;
        }
        if (item.percentValue < leastCommon.percentValue) {
          leastCommon = item;
        }
      }
      
      distributionInsights.mostCommonRating = mostCommon.value;
      distributionInsights.mostCommonPercentage = mostCommon.percentValue;
      distributionInsights.leastCommonRating = leastCommon.value;
      distributionInsights.leastCommonPercentage = leastCommon.percentValue;
      
      // Calculate distribution relative to midpoint
      const midpoint = (scaleRange + 1) / 2; // e.g., 3 for 5-point scale, 5.5 for 10-point
      
      let belowMidpoint = 0;
      let atMidpoint = 0;
      let aboveMidpoint = 0;
      
      for (const item of scaleDistribution) {
        const value = parseFloat(item.value);
        if (!isNaN(value)) {
          if (value < midpoint) {
            belowMidpoint += item.percentValue;
          } else if (value === midpoint) {
            atMidpoint += item.percentValue;
          } else {
            aboveMidpoint += item.percentValue;
          }
        }
      }
      
      distributionInsights.belowMidpointPercentage = belowMidpoint;
      distributionInsights.atMidpointPercentage = atMidpoint;
      distributionInsights.aboveMidpointPercentage = aboveMidpoint;
      
      // console.log('📈 Scale distribution insights:', distributionInsights);
    }
    
    // Format the data for inclusion in the prompt
    const questionText = eventDetails.questionText || 
                        eventDetails.question_text || 
                        eventDetails.question || 
                        eventDetails.prompt || 
                        eventDetails.title || 
                        eventDetails.content || 
                        'Rate on a scale';
    
    // Format prompt sections
    const formattedParticipationData = `Total Users: ${totalUsers || 'Unknown'}
Total Responses: ${totalResponses || 'Unknown'}
Participation Rate: ${participationRate ? participationRate + '%' : 'Unknown'}`;
    
    // Format scale distribution if available
    let formattedDistribution = 'No scale distribution data available';
    if (scaleDistribution.length > 0) {
      formattedDistribution = scaleDistribution.map(item => {
        let line = `Rating ${item.value}`;
        if (item.description) {
          line += ` - ${item.description}`;
        }
        line += `: ${item.percentage} (${item.count} responses)`;
        return line;
      }).join('\n');
    }
    
    // Format distribution insights if available
    let formattedInsights = '';
    if (scaleDistribution.length > 0) {
      formattedInsights = `
Most Common Rating: ${distributionInsights.mostCommonRating} (${distributionInsights.mostCommonPercentage}%)
Least Common Rating: ${distributionInsights.leastCommonRating} (${distributionInsights.leastCommonPercentage}%)
Below Midpoint: ${distributionInsights.belowMidpointPercentage}%
At Midpoint: ${distributionInsights.atMidpointPercentage}%
Above Midpoint: ${distributionInsights.aboveMidpointPercentage}%`;
    }
    
    // Format comments section
    const formattedComments = comments.length > 0
      ? comments.map((comment, index) => {
          return `${index + 1}. "${comment.content}" - ${comment.author}${comment.likes ? ` (${comment.likes} likes)` : ''}`;
        }).join('\n')
      : 'No comments available';
    
    // Assemble the data section
    const formattedData = `Question: ${questionText}
Question Type: ${questionType}
${formattedParticipationData}
Average Rating: ${averageScore || 'Unknown'}${formattedInsights}`;
    
    // Initialize the prompt template - ensuring we only use real available data
    // console.log('📚 Final verification of scale prompt data integrity');
    // console.log('📊 Only using real, available data points - no estimations');
    
    const promptTemplate = `# Scale Rating Analysis Task

You are an expert data analyst analyzing a Rating Scale survey. This type of survey asks respondents to rate items on a numeric scale. Analyze the following data and provide a structured report in markdown format:

## Survey Question
${questionText}

## Survey Details
${formattedData}

## Rating Distribution
${formattedDistribution}

## User Comments (${comments.length} comments)
${formattedComments}

## Analysis Request
Provide a comprehensive analysis of this scale rating exercise. Consider patterns, trends, and insights about participant ratings. Structure your analysis with these sections:

# Executive Summary
- [Provide a concise 3-5 sentence overview of the key findings]
- [Highlight the most significant insights from the rating data]
- [Summarize overall participant sentiment and trends]
- [Note any standout patterns or trends]

# Key Themes
- [Identify 3-5 major themes from the rating data]
- [Focus on underlying sentiments, values, and priorities]
- [Include both positive aspects and areas of concern]
- [Highlight patterns in how participants rated the question]
- [Analyze the distribution pattern (e.g., skewed, normal, bimodal)]

# Recommendations
- [Provide 3-5 actionable next steps based on the rating data]
- [Include specific suggestions for addressing concerns]
- [Recommend strategies for leveraging positive feedback]
- [Suggest how to address any polarized ratings if present]
- [Offer ideas for follow-up actions to gain deeper insights]

# Limitations
- [List any limitations in the data that affect the depth or breadth of analysis]
- [Suggest what additional data would be helpful]
- [Identify potential biases or gaps in the responses]
- [Comment on data quality issues if present]

# Survey Design Feedback
- [Provide suggestions for improving the survey question or format]
- [Comment on the rating scale effectiveness]
- [Suggest improvements to the question wording if needed]
- [Suggest how to generate richer, more actionable, or more diverse responses]

Format your response with these EXACT section headings using single # for each section. Ensure each section has substantive content.`;

    // console.log('✅ Scale prompt generated successfully:', {
    //   promptLength: promptTemplate.length,
    //   hasDistributionData: scaleDistribution.length > 0,
    //   timestamp: new Date().toISOString()
    // });

    return promptTemplate;
  } catch (error) {
    console.error('❌ Error in generateScalePrompt:', error.message, error.stack);
    // If an error occurs, use the fallback prompt
    return getScaleFallbackPrompt();
  }
};
